// Compute (start_date, end_date) for a Golf pick_em pool given the
// event's actual tournament dates from DataGolf's field-updates response.
//
// Replaces the duration-based math in calculateGroupDates (db.js:31) for
// the Golf pick_em path specifically. The original incident: a Golf pool
// created with duration_type='daily' got end_date = start + 1 day, which
// landed BEFORE the tournament finished → overdue-finalize sweep flipped
// status='completed' after R1, three rounds remaining.
//
// Why this lives in its own file (and not inline in db.js):
// db.js depends on import.meta.env / supabase / localStorage — none of
// which are available under `node --test`. Pulling the pure date math
// out makes it unit-testable AND keeps db.js a thin client of the
// helper rather than carrying golf-specific knowledge inline.
//
// Why +1 day buffer on dateEnd:
// The tournament's date_end is the closing round's calendar day (Sun
// for a standard PGA Thu-Sun event). A Monday weather finish would
// push the actual round-end past midnight UTC; the grader cron may
// also need a tick or two after R4 finishes before pending picks are
// fully scored. The +1 day buffer keeps the sweep from racing those.
//
// Fallback (dateEnd null):
// When the event isn't yet in field-updates (focus mismatch, or
// pre-event window), dateEnd reads null. Use startDate + 4 days + 1
// buffer = startDate + 5 days. Caller should console.warn naming the
// event so the operator can audit any fallback fires before the next
// sweep window. The PGA Tour Thu-Sun assumption is the same one
// getRoundStatus uses internally (golfData.js:738); reliable for every
// event the pool flow targets.
//
// All date arithmetic is in UTC. The original bug was partly a
// local-tz off-by-one in calculateGroupDates: Date('2026-06-11') parsed
// as UTC midnight prints as June 10 evening in EDT; setDate(+1) lands
// on local June 11 which prints as June 12 UTC … or, depending on
// when the arithmetic ran, June 11 UTC. UTC-anchored parsing here
// makes the result tz-invariant.

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * @param {{startDate: string|null, dateEnd: string|null}} input
 *   YYYY-MM-DD strings from DataGolf — `startDate` from the schedule
 *   entry, `dateEnd` from getEventMeta (field-updates response).
 * @returns {{startISO: string, endISO: string, usedFallback: boolean}}
 *   ISO date strings ready for the create_group_atomic payload.
 *   `usedFallback` is true when dateEnd was missing/malformed and the
 *   +5-day heuristic was used — caller should warn the operator so
 *   the fallback fires don't go unnoticed.
 * @throws {Error} when startDate is missing or malformed — the create
 *   flow is non-functional without it, so loud failure beats a silent
 *   "today + 5" pool.
 */
export function computeGolfPoolDates({ startDate, dateEnd }) {
  if (!startDate) {
    throw new Error('computeGolfPoolDates: startDate is required')
  }
  const startMs = Date.parse(startDate + 'T00:00:00Z')
  if (Number.isNaN(startMs)) {
    throw new Error(`computeGolfPoolDates: invalid startDate ${JSON.stringify(startDate)}`)
  }

  let endMs
  let usedFallback = false
  if (dateEnd) {
    const dateEndMs = Date.parse(dateEnd + 'T00:00:00Z')
    if (Number.isNaN(dateEndMs)) {
      endMs = startMs + 5 * DAY_MS
      usedFallback = true
    } else {
      endMs = dateEndMs + DAY_MS
    }
  } else {
    endMs = startMs + 5 * DAY_MS
    usedFallback = true
  }

  return {
    startISO:    new Date(startMs).toISOString().slice(0, 10),
    endISO:      new Date(endMs).toISOString().slice(0, 10),
    usedFallback,
  }
}
